import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { CheckCircle, CreditCard, User, Calendar, Euro } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SimulationStep3 = () => {
  const navigate = useNavigate();
  const [step1Data, setStep1Data] = useState(null);
  const [step2Data, setStep2Data] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const s1 = sessionStorage.getItem('simulation_step1');
    const s2 = sessionStorage.getItem('simulation_step2');
    
    if (!s1 || !s2) {
      navigate('/simulation/step1');
      return;
    }
    
    setStep1Data(JSON.parse(s1));
    setStep2Data(JSON.parse(s2));
  }, [navigate]);

  if (!step1Data || !step2Data) return null;

  // Calculate monthly payment
  const taeg = 5.9;
  const monthlyRate = taeg / 100 / 12;
  const monthlyPayment = (step1Data.amount * monthlyRate * Math.pow(1 + monthlyRate, step1Data.duration)) / (Math.pow(1 + monthlyRate, step1Data.duration) - 1);
  const totalAmount = monthlyPayment * step1Data.duration;

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/credit-requests/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectType: step1Data.projectType,
          projectTypeLabel: step1Data.projectTypeLabel,
          amount: step1Data.amount,
          duration: step1Data.duration,
          monthlyPayment: parseFloat(monthlyPayment.toFixed(2)),
          taeg: taeg,
          totalAmount: parseFloat(totalAmount.toFixed(2)),
          civility: step2Data.civility,
          lastName: step2Data.lastName,
          firstName: step2Data.firstName,
          birthDate: step2Data.birthDate,
          email: step2Data.email,
          phone: step2Data.phone
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        // Clear session storage
        sessionStorage.removeItem('simulation_step1');
        sessionStorage.removeItem('simulation_step2');
      } else {
        alert('Erreur lors de la soumission. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Erreur lors de la soumission. Veuillez réessayer.');
    }
    
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-black py-4 px-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <img 
              src="https://customer-assets.emergentagent.com/job_younited-clone-1/artifacts/y6hpz6h2_IMG_0359.jpeg"
              alt="Younited"
              className="h-10 object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="bg-green-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="h-14 w-14 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Demande envoyée avec succès !
          </h1>
          <p className="text-gray-600 mb-8">
            Votre demande de crédit a été enregistrée. Un conseiller vous contactera dans les plus brefs délais.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="h-14 px-8 text-white font-bold text-lg rounded-xl"
            style={{ backgroundColor: '#8B5CF6' }}
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_younited-clone-1/artifacts/y6hpz6h2_IMG_0359.jpeg"
            alt="Younited"
            className="h-10 object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <Button 
            variant="outline" 
            className="text-white border-white hover:bg-white hover:text-black rounded-full px-6"
            onClick={() => navigate('/login')}
          >
            Espace Client
          </Button>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center space-x-8">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">✓</div>
              <span className="text-green-600 font-medium">Votre projet</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">✓</div>
              <span className="text-green-600 font-medium">Vos coordonnées</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-sm">3</div>
              <span className="text-purple-600 font-medium">Récapitulatif</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="p-8 shadow-lg">
          <h1 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Récapitulatif de votre demande
          </h1>

          {/* Project Summary */}
          <div className="bg-purple-50 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <CreditCard className="h-6 w-6 text-purple-600" />
              <h3 className="font-bold text-gray-900">Votre projet</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Type de projet</p>
                <p className="font-semibold text-gray-900">{step1Data.projectTypeLabel}</p>
              </div>
              <div>
                <p className="text-gray-500">Montant</p>
                <p className="font-semibold text-gray-900">{step1Data.amount.toLocaleString()} €</p>
              </div>
              <div>
                <p className="text-gray-500">Durée</p>
                <p className="font-semibold text-gray-900">{step1Data.duration} mois</p>
              </div>
              <div>
                <p className="text-gray-500">TAEG</p>
                <p className="font-semibold text-gray-900">{taeg}%</p>
              </div>
            </div>
          </div>

          {/* Contact Summary */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-3 mb-4">
              <User className="h-6 w-6 text-gray-600" />
              <h3 className="font-bold text-gray-900">Vos coordonnées</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Civilité</p>
                <p className="font-semibold text-gray-900">{step2Data.civility === 'M' ? 'Monsieur' : 'Madame'}</p>
              </div>
              <div>
                <p className="text-gray-500">Nom complet</p>
                <p className="font-semibold text-gray-900">{step2Data.firstName} {step2Data.lastName}</p>
              </div>
              <div>
                <p className="text-gray-500">Date de naissance</p>
                <p className="font-semibold text-gray-900">{new Date(step2Data.birthDate).toLocaleDateString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-semibold text-gray-900">{step2Data.email}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-500">Téléphone</p>
                <p className="font-semibold text-gray-900">{step2Data.phone}</p>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="bg-green-50 rounded-xl p-6 mb-8 text-center border-2 border-green-200">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Euro className="h-6 w-6 text-green-600" />
              <h3 className="font-bold text-gray-900">Votre mensualité</h3>
            </div>
            <p className="text-4xl font-bold text-green-600 mb-2">{monthlyPayment.toFixed(2)} € <span className="text-lg font-normal text-gray-500">/mois</span></p>
            <p className="text-sm text-gray-600">Montant total: {totalAmount.toFixed(2)} €</p>
          </div>

          {/* Buttons */}
          <div className="flex space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/simulation/step2')}
              className="flex-1 h-14 font-bold text-lg rounded-xl"
            >
              Retour
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 h-14 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              style={{ backgroundColor: '#8B5CF6' }}
            >
              {loading ? 'Envoi en cours...' : 'Valider ma demande'}
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-6">
            En validant, vous acceptez nos conditions générales. Un crédit vous engage et doit être remboursé. 
            Vérifiez vos capacités de remboursement avant de vous engager.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default SimulationStep3;
